import React from 'react';
import { scrollOpen } from 'js/pages/BrowsePage/browse-scroll';
import * as track from 'js/pages/BrowsePage/tracking/browse-result';
import { Inline, Text, Stack } from '@treatwell/ui';

import { Button } from 'js/components/Button';
import { VenueOpeningTimes } from 'js/components/VenueOpeningTimes';
import { Chevron, Direction } from 'js/components/Icons/Chevron/Chevron';
import { Collapsable } from 'js/components/Collapsable/Collapsable';
import { VenueRating } from 'js/model/rainbow/venue/VenueOutput';
import { VenueTagData } from 'js/components/VenueTag/VenueTag';
import { Action, Category } from '../tracking/tracking';
import styles from './BrowseResultQuickview.module.css';
import { trackVenueResultData } from './tracking';
import { MenuHighlight } from './BrowseResult';

interface Props {
  openingHours: VenueOpeningTimes['props']['items'];
  description?: string;
  openLabel: string;
  closeLabel: string;
  buttonLabel: string;
  onGoToVenueButtonClick: (event: React.MouseEvent) => void;
  isInHybridView?: boolean;
  menuHighlights: MenuHighlight[];
  venueId: number;
  rating: VenueRating;
  tags: VenueTagData[];
  countryCode: string;
  venueRank?: number;
  isNewVenue?: boolean;
  onQuickviewTabClick?: (isOpen: boolean) => void;
}

interface State {
  isOpen: boolean;
}

export class BrowseResultQuickview extends React.PureComponent<Props, State> {
  private container: React.RefObject<HTMLDivElement | null> = React.createRef();

  private contentContainer: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  public state: State = {
    isOpen: false,
  };

  private onQuickviewTabClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    const category = this.props.isInHybridView
      ? Category.BrowseHybridResults
      : Category.BrowseListResults;

    if (!this.state.isOpen) {
      trackVenueResultData({
        eventName: 'search_venue_details_clicked',
        menuHighlights: this.props.menuHighlights,
        venueId: this.props.venueId,
        rating: this.props.rating,
        tags: this.props.tags,
        countryCode: this.props.countryCode,
        venueRank: this.props.venueRank,
        isNewVenue: this.props.isNewVenue,
      });
    }

    this.setState(
      (state) => ({ isOpen: !state.isOpen }),
      () => {
        if (this.state.isOpen) {
          scrollOpen(this.container, this.contentContainer);
        }

        track.toggleQuickview(
          category,
          this.state.isOpen ? Action.Open : Action.Close
        );
        this.props.onQuickviewTabClick?.(this.state.isOpen);
      }
    );
  };

  private renderContent(): React.ReactNode {
    return (
      <div className={styles.content}>
        <Stack space="md">
          <div ref={this.contentContainer}>
            <Inline wrap align="start">
              <VenueOpeningTimes
                items={this.props.openingHours}
                positioningClassName={styles.openingTimesContainer}
              />
              {this.props.description !== undefined && (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: this.props.description }}
                />
              )}
            </Inline>
          </div>
          <div className={styles.divider} />
          <span
            className={styles.cta}
            onClick={this.props.onGoToVenueButtonClick}
          >
            <Button
              positioningClassname={styles.button}
              label={this.props.buttonLabel}
            />
          </span>
        </Stack>
      </div>
    );
  }

  public render(): React.ReactNode {
    const toggleCTA = this.state.isOpen
      ? this.props.closeLabel
      : this.props.openLabel;

    const direction = this.state.isOpen ? Direction.Up : Direction.Down;

    return (
      <div ref={this.container}>
        <div className={styles.tab} onClick={this.onQuickviewTabClick}>
          <Inline justify="between">
            <Text type="bodyHeavy" className={styles.cta}>
              {toggleCTA}
            </Text>
            <Chevron direction={direction} animated />
          </Inline>
        </div>
        <Collapsable isVisible={this.state.isOpen}>
          {this.renderContent()}
        </Collapsable>
      </div>
    );
  }
}
